import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Download, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getGoogleDriveVideoUrl } from '../utils/mediaUtils';

export interface GalleryItem {
  id: number;
  url: string;
  type: 'image' | 'video';
  caption?: string;
}

interface LightboxGalleryProps {
  items: GalleryItem[];
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
}

export const LightboxGallery = ({ items, initialIndex, isOpen, onClose }: LightboxGalleryProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex]);

  if (!isOpen || items.length === 0) return null;

  const currentItem = items[currentIndex];
  const isVideo = currentItem.type === 'video';

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
  };

  const handleDownload = async () => {
    try {
      // Try to download
      const response = await fetch(currentItem.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${isVideo ? 'video' : 'image'}-${currentItem.id}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Fallback: open in new tab
      console.error('Download failed, opening in new tab:', error);
      window.open(currentItem.url, '_blank');
    }
  };

  const handleOpenInNewTab = () => {
    window.open(currentItem.url, '_blank');
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
        aria-label="Đóng"
      >
        <X className="h-6 w-6" />
      </button>

      {/* Navigation Buttons */}
      {items.length > 1 && (
        <>
          <button
            onClick={handlePrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            aria-label="Ảnh trước"
          >
            <ChevronLeft className="h-8 w-8" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            aria-label="Ảnh sau"
          >
            <ChevronRight className="h-8 w-8" />
          </button>
        </>
      )}

      {/* Action Buttons */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleDownload}
          className="bg-black/50 hover:bg-black/70 text-white border-white/20"
        >
          <Download className="h-4 w-4 mr-2" />
          Tải xuống
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleOpenInNewTab}
          className="bg-black/50 hover:bg-black/70 text-white border-white/20"
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          Mở tab mới
        </Button>
      </div>

      {/* Counter */}
      {items.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 px-4 py-2 bg-black/50 rounded-full text-white text-sm">
          {currentIndex + 1} / {items.length}
        </div>
      )}

      {/* Content */}
      <div className="w-full h-full flex items-center justify-center p-8">
        {isVideo ? (
          <div className="w-full max-w-5xl">
            <iframe
              src={getGoogleDriveVideoUrl(currentItem.url)}
              className="w-full aspect-video"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              title={currentItem.caption || 'Video'}
            />
            {currentItem.caption && (
              <p className="text-white text-center mt-4">{currentItem.caption}</p>
            )}
          </div>
        ) : (
          <div className="max-w-7xl max-h-full flex flex-col items-center">
            <img
              src={currentItem.url}
              alt={currentItem.caption || `Image ${currentIndex + 1}`}
              className="max-w-full max-h-[calc(100vh-12rem)] object-contain"
            />
            {currentItem.caption && (
              <p className="text-white text-center mt-4">{currentItem.caption}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
